import * as React from "react";
import { Checkbox } from "./ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Edit, EyeOff, Zap } from "lucide-react";
import { TbWindowMaximize, TbWindowMinimize } from "react-icons/tb";
import { ICompassMessage } from "@/types/compass.types";

interface BulkActionButtonsProps {
  message: ICompassMessage;
  isExpanded: boolean;
  toggleExpandAll: () => void;
  onEditAll: () => void;
  onBulkActionToggle: (checked: boolean) => void;
  onOneClickAutomation: () => void;
  isAnyAction: boolean;
  isBulkActionChecked: boolean;
  isGlobalEditMode: boolean;
}

const buildBulkActions = (props: BulkActionButtonsProps) => {
  const {
    isExpanded,
    toggleExpandAll,
    onEditAll,
    onBulkActionToggle,
    onOneClickAutomation,
    isAnyAction,
    isBulkActionChecked,
    isGlobalEditMode,
    message,
  } = props;
  return [
    {
      id: "one-click-automation",
      type: "button" as const,
      icon: (
        <div className="rounded-full animate-pulse-bolt">
          <Zap className="size-3.5 text-warning-200" />
        </div>
      ),
      tooltipContent: "Create 1-click-automation from actions",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onOneClickAutomation();
      },
    },
    {
      id: "toggle-expand-all",
      type: "button" as const,
      icon: isExpanded ? (
        <TbWindowMinimize className="size-3.5" />
      ) : (
        <TbWindowMaximize className="size-3.5" />
      ),
      tooltipContent: isExpanded ? "Minimize all" : "Maximize all",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleExpandAll();
      },
    },
    {
      id: "edit-all-requests",
      type: "button" as const,
      icon: isGlobalEditMode ? <EyeOff className="size-3.5" /> : <Edit className="size-3.5" />,
      tooltipContent: isGlobalEditMode ? "Exit edit mode" : "Edit all requests",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onEditAll();
      },
    },
    {
      id: "select-bulk-actions",
      type: "checkbox" as const,
      checked: isBulkActionChecked,
      onCheckedChange: onBulkActionToggle,
      tooltipContent: isBulkActionChecked
        ? "Cancel all pending actions"
        : "Select all and run bulk actions",
      disabled: !isAnyAction,
    },
  ].filter((action) => {
    if (action.id === "one-click-automation") {
      return (message?.actions?.length ?? 0) > 1;
    }
    return true;
  });
};

const BulkActionButtons: React.FC<BulkActionButtonsProps> = (props) => {
  const bulkActions = buildBulkActions(props);

  return (
    <>
      {bulkActions.map((action) => (
        <TooltipProvider key={action.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              {action.type === "button" ? (
                <span onClick={action.onClick} className="cursor-pointer">
                  {action.icon}
                </span>
              ) : (
                <Checkbox
                  checked={action.checked}
                  onCheckedChange={action.onCheckedChange}
                  className="scale-90"
                  disabled={action.disabled}
                />
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{action.tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </>
  );
};

export { BulkActionButtons };
